import { httpClient } from '@/api/http-client';
import { CommentableEntityType as CommentableEntityTypeEnum } from '@carvia/ros-client-types';

/** Wire/value form of the generated enum — string literals work in Vue templates. */
export type CommentableEntityType = `${CommentableEntityTypeEnum}`;

export type TipTapNode = {
  attrs?: Record<string, unknown>;
  content?: TipTapNode[];
  marks?: Array<{ attrs?: Record<string, unknown>; type: string }>;
  text?: string;
  type: string;
};

export type CommentAuthor = {
  firstName: string;
  id: string;
  lastName: string;
};

export type CommentReaction = {
  emoji: string;
  users: CommentAuthor[];
};

export type CommentThread = {
  author: CommentAuthor;
  body: TipTapNode;
  createdDate: string;
  id: string;
  mentionedUserIds: string[];
  parentId: string | null;
  reactions: CommentReaction[];
  replies: CommentThread[];
  resolvedAt: string | null;
  resolvedBy: CommentAuthor | null;
  updatedDate: string;
};

export type CommentMentionCandidate = {
  firstName: string;
  id: string;
  lastName: string;
};

export type CommentTarget = {
  entityId: string;
  entityType: CommentableEntityType;
};

const path = ({ entityType, entityId }: CommentTarget) => `/comments/${entityType}/${entityId}`;

class CommentsService {
  create(target: CommentTarget, body: { body: TipTapNode; parentId?: string | null }) {
    return httpClient.post<CommentThread>(path(target), body).then((res) => res.data);
  }

  update(target: CommentTarget, commentId: string, body: { body: TipTapNode }) {
    return httpClient
      .patch<CommentThread>(`${path(target)}/${commentId}`, body)
      .then((res) => res.data);
  }

  resolve(target: CommentTarget, commentId: string) {
    return httpClient
      .post<CommentThread>(`${path(target)}/${commentId}/resolve`)
      .then((res) => res.data);
  }

  unresolve(target: CommentTarget, commentId: string) {
    return httpClient
      .post<CommentThread>(`${path(target)}/${commentId}/unresolve`)
      .then((res) => res.data);
  }

  remove(target: CommentTarget, commentId: string) {
    return httpClient.delete(`${path(target)}/${commentId}`).then(() => undefined);
  }

  toggleReaction(target: CommentTarget, commentId: string, emoji: string) {
    return httpClient
      .post<CommentThread>(`${path(target)}/${commentId}/reactions`, { emoji })
      .then((res) => res.data);
  }

  mentionCandidates(target: CommentTarget, query: string) {
    return httpClient
      .get<{ items: CommentMentionCandidate[] }>(`${path(target)}/mention-candidates`, {
        params: { query },
      })
      .then((res) => res.data.items);
  }
}

export const commentsService = new CommentsService();
